// CRM 2.0 · M12 Template Builder: Settings (prototype vSettings). Part 2 has the Suggestions tab with the field
// marker names, and the Approval tab (comments left in the Word file: warn or stop sending): the name Legal types in the Word file ("{Flat No}") -> a field. Names Legal assigns by hand are
// learned when a version is approved (From: Learned). Saved in Crm2Settings · TEMPLATE_BUILDER.suggestRules.
import React, { useEffect, useMemo, useState } from "react";
import cls from "./Templates.module.css";
import { listDocFields, getTemplateBuilderSettings, saveTemplateBuilderSettings, listRoles } from "../../Services/Templates";
import { errorTost, successTost } from "../../Services/Toast";
import { presentLoader, dismissLoader } from "../../Services/loader";
import FieldOrderModal from "./FieldOrderModal";
import { partyDefaults } from "./templateCommon";

const FROM = { SEED: "Starter list", LEARNED: "Learned at approval", LEGAL: "Added in Settings" };

function MarkerModal({ rule, fields, onCancel, onSave }) {
  const [name, setName] = useState(rule.pattern || "");
  const [fieldKey, setFieldKey] = useState(rule.fieldKey || "");
  const [isActive, setIsActive] = useState(rule.isActive !== false);
  const [q, setQ] = useState("");
  const groups = useMemo(() => {
    const query = q.trim().toLowerCase();
    const byGroup = {};
    fields.filter((f) => !query || f.label.toLowerCase().includes(query) || f.key.toLowerCase().includes(query))
      .forEach((f) => { (byGroup[f.fieldGroup] = byGroup[f.fieldGroup] || []).push(f); });
    return byGroup;
  }, [fields, q]);
  const clean = name.replace(/[{}]/g, "").replace(/\s+/g, " ").trim();
  return (
    <div className={cls.mask} role="dialog" aria-modal="true" aria-label="Field marker name">
      <div className={cls.modal}>
        <div className={cls.modalHead}>
          <h2>{rule.pattern ? "Edit marker name" : "Add marker name"}</h2>
          <p className={cls.hint}>What Legal types inside {"{ }"} in the Word file, and the field it suggests.</p>
        </div>
        <div className={cls.modalBody}>
          <div>
            <label className={cls.lbl} htmlFor="markerName">Name in the Word file</label>
            <input id="markerName" className={cls.inp} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Flat No" />
            <p className={cls.hint}>Typed as {`{${clean || "Flat No"}}`}. Capitals and spaces do not matter.</p>
          </div>
          <div>
            <label className={cls.lbl} htmlFor="markerFieldSearch">Field</label>
            <input id="markerFieldSearch" className={cls.inp} placeholder="Search fields" value={q} onChange={(e) => setQ(e.target.value)} style={{ marginBottom: ".8rem" }} />
            <select className={cls.sel} size={8} value={fieldKey} onChange={(e) => setFieldKey(e.target.value)} aria-label="Field">
              {Object.entries(groups).map(([g, list]) => (
                <optgroup key={g} label={g}>
                  {list.map((f) => <option key={f.key} value={f.key}>{f.label} · {f.key}</option>)}
                </optgroup>
              ))}
            </select>
          </div>
          <label className={cls.chk}><input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> Active (suggested for new uploads)</label>
        </div>
        <div className={cls.modalFoot}>
          <button className={cls.btn} onClick={onCancel}>Cancel</button>
          <button className={`${cls.btn} ${cls.primary}`} disabled={!clean || !fieldKey} onClick={() => onSave({ ...rule, pattern: clean, fieldKey, isActive })}>Save</button>
        </div>
      </div>
    </div>
  );
}

// the fields the printed copy may print with a newer value than the final draft (TEMPLATE_BUILDER.printTimeFields);
// the backend uses the same list while the setting is not saved yet (services/documents/printedCopy.js)
const DEFAULT_PRINT_TIME_FIELDS = [
  "booking.crmName", "bank.loanBank",
  "price.paidAmount", "price.paidAmountWords", "price.paidGst", "price.paidGstWords", "price.paidSdr", "price.paidSdrWords",
  "price.paidTotal", "price.paidTotalWords", "price.balanceAmount", "price.balanceAmountWords",
];

function PrintTimeFieldsModal({ fields, initial, onCancel, onSave }) {
  const [keys, setKeys] = useState(() => new Set(initial));
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const byGroup = {};
  fields.filter((f) => !query || f.label.toLowerCase().includes(query) || f.key.toLowerCase().includes(query))
    .forEach((f) => { (byGroup[f.fieldGroup] = byGroup[f.fieldGroup] || []).push(f); });
  const toggle = (k) => setKeys((s) => { const next = new Set(s); if (next.has(k)) next.delete(k); else next.add(k); return next; });
  return (
    <div className={cls.mask} role="dialog" aria-modal="true" aria-label="Print-time fields">
      <div className={cls.modal} style={{ maxWidth: "64rem" }}>
        <div className={cls.modalHead}>
          <h2>Fields that may change after the final draft</h2>
          <p className={cls.hint}>Ticked fields print with the booking's value at print time. Any other field that changed stops printing.</p>
        </div>
        <div className={cls.modalBody}>
          <input className={cls.inp} placeholder="Search fields" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search fields" />
          <div style={{ maxHeight: "40rem", overflowY: "auto" }}>
            {Object.entries(byGroup).map(([g, list]) => (
              <div key={g} style={{ marginBottom: "1.2rem" }}>
                <p className={cls.lbl}>{g}</p>
                {list.map((f) => (
                  <label key={f.key} className={cls.chk} style={{ display: "flex" }}>
                    <input type="checkbox" checked={keys.has(f.key)} onChange={() => toggle(f.key)} /> {f.label} <span className={cls.muted}>· {f.key}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className={cls.modalFoot}>
          <button className={cls.btn} onClick={onCancel}>Cancel</button>
          <button className={`${cls.btn} ${cls.primary}`} onClick={() => onSave(fields.map((f) => f.key).filter((k) => keys.has(k)))}>Save</button>
        </div>
      </div>
    </div>
  );
}

function TemplateSettings({ canEdit }) {
  const [settings, setSettings] = useState(null); // { values, lastUpdatedAt }
  const [fields, setFields] = useState([]);
  const [editing, setEditing] = useState(null); // { rule, index } (index -1 = new)
  const [tab, setTab] = useState("SUGGESTIONS");
  const [q, setQ] = useState("");
  const [pick, setPick] = useState(null); // "buyerFields" | "signFields": the field pop-up
  const [printPick, setPrintPick] = useState(false); // the print-time fields pop-up
  const [roles, setRoles] = useState([]); // for "who sees full values when a QR is scanned"

  useEffect(() => {
    let live = true;
    presentLoader();
    Promise.all([getTemplateBuilderSettings(), listDocFields()])
      .then(([s, f]) => { if (live) { setSettings(s); setFields(f || []); } })
      .catch((e) => errorTost(e.message))
      .finally(dismissLoader);
    listRoles().then((r) => live && setRoles(r || [])).catch(() => {});
    return () => { live = false; };
  }, []);

  const rules = settings?.values?.suggestRules || [];
  const labelOf = (key) => (fields.find((f) => f.key === key) || {}).label || key;
  const query = q.trim().toLowerCase();
  const markers = rules.map((r, index) => ({ r, index })).filter(({ r }) => r.ruleKind === "MARKER")
    .filter(({ r }) => !query || String(r.pattern).toLowerCase().includes(query) || labelOf(r.fieldKey).toLowerCase().includes(query))
    .sort((a, b) => String(a.r.pattern).localeCompare(String(b.r.pattern)));
  const markerPattern = rules.some((r) => r.ruleKind === "BLANK_PATTERN" && r.blankKind === "MARKER" && r.isActive !== false);

  async function saveValues(change, message) {
    try {
      const saved = await saveTemplateBuilderSettings({ ...settings.values, ...change }, settings.lastUpdatedAt);
      setSettings(saved);
      successTost(message || "Settings saved");
    } catch (e) {
      errorTost(e.message);
    }
  }

  async function save(rule, index) {
    const list = [...rules];
    if (index >= 0) list[index] = rule;
    else list.push({ ...rule, ruleKind: "MARKER", source: "LEGAL", sortOrder: rules.reduce((m, r) => Math.max(m, Number(r.sortOrder) || 0), 0) + 1 });
    try {
      const saved = await saveTemplateBuilderSettings({ ...settings.values, suggestRules: list }, settings.lastUpdatedAt);
      setSettings(saved);
      setEditing(null);
      successTost("Settings saved");
    } catch (e) {
      errorTost(e.message);
    }
  }

  if (!settings) return null;
  return (
    <div>
      <div className={cls.tabs} role="tablist" aria-label="Settings">
        {[["SUGGESTIONS", "Suggestions"], ["PARTIES", "Buyer & signatures"], ["DRAFTS", "Booking drafts"], ["APPROVAL", "Approval"]].map(([k, t]) => (
          <button key={k} className={`${cls.tab} ${tab === k ? cls.tabOn : ""}`} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{t}</button>
        ))}
      </div>
      {tab === "DRAFTS" && (
        <div className={cls.panel}>
          <h2 className={cls.panelTitle}>Review draft</h2>
          <label className={cls.lbl} htmlFor="reviewDraftMissing">Where the booking has no value yet, print</label>
          <select id="reviewDraftMissing" className={cls.sel} style={{ width: "40rem" }} disabled={!canEdit}
            value={String(settings.values.reviewDraftMissing || "FIELD_NAME").toUpperCase() === "BLANK" ? "BLANK" : "FIELD_NAME"}
            onChange={(e) => saveValues({ reviewDraftMissing: e.target.value })}>
            <option value="FIELD_NAME">The field name, e.g. [Applicant first name]</option>
            <option value="BLANK">Nothing (blank)</option>
          </select>
          <p className={cls.hint}>
            With the field name, a part with no rows yet (no applicants, parking or payment plan) prints once with the field names, so the
            customer sees what comes there. The final draft is not affected: a missing value always stops it. Saved as soon as you change it.
          </p>
          <h2 className={cls.panelTitle} style={{ marginTop: "2.4rem" }}>Printed copy</h2>
          <p className={cls.lbl}>Fields that may change after the final draft</p>
          {(() => {
            const list = Array.isArray(settings.values.printTimeFields) ? settings.values.printTimeFields : DEFAULT_PRINT_TIME_FIELDS;
            return <p style={{ margin: 0 }}>{list.length ? list.map(labelOf).join(", ") : "None"}{Array.isArray(settings.values.printTimeFields) ? "" : " (starter list)"}</p>;
          })()}
          {canEdit && <button className={cls.btn} style={{ marginTop: ".8rem" }} onClick={() => setPrintPick(true)}>Change…</button>}
          <p className={cls.hint}>
            The printed copy is the final draft again with its QR codes. These fields print with the booking's value at print time (for
            example a payment made after the final draft). Any other field that changed stops printing with "Booking data changed since
            final draft version N: make a new final draft".
          </p>
          <h2 className={cls.panelTitle} style={{ marginTop: "2.4rem" }}>QR scan</h2>
          <p className={cls.lbl}>Who sees PAN, Aadhaar, mobile and email in full when a QR is scanned</p>
          {(() => {
            const saved = Array.isArray(settings.values.qrFullDataRoleIds);
            const signatory = roles.find((r) => r.crm2Code === "AUTHORISED_SIGNATORY");
            const ids = saved ? settings.values.qrFullDataRoleIds.map(String) : signatory ? [String(signatory.roleId)] : [];
            const toggle = (id) => saveValues({ qrFullDataRoleIds: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] });
            return (
              <>
                <div style={{ display: "flex", flexWrap: "wrap", gap: ".4rem 2rem", maxWidth: "80rem" }}>
                  {roles.map((r) => (
                    <label key={r.roleId} className={cls.chk}>
                      <input type="checkbox" disabled={!canEdit} checked={ids.includes(String(r.roleId))} onChange={() => toggle(String(r.roleId))} /> {r.title}
                    </label>
                  ))}
                </div>
                <p className={cls.hint}>
                  Every other logged-in user sees them masked (PAN ABXXXXXX4F, mobile XXXXXX3210, email ha****@gmail.com).
                  {saved ? "" : " Not saved yet: the Authorised Signatory role is used."} Saved as soon as you change it.
                </p>
              </>
            );
          })()}
        </div>
      )}
      {printPick && (
        <PrintTimeFieldsModal fields={fields}
          initial={Array.isArray(settings.values.printTimeFields) ? settings.values.printTimeFields : DEFAULT_PRINT_TIME_FIELDS}
          onCancel={() => setPrintPick(false)}
          onSave={(keys) => { setPrintPick(false); saveValues({ printTimeFields: keys }); }} />
      )}
      {tab === "APPROVAL" && (
        <div className={cls.panel}>
          <h2 className={cls.panelTitle}>Before sending for approval</h2>
          <label className={cls.lbl} htmlFor="commentsLeft">Comments left in the Word file</label>
          <select id="commentsLeft" className={cls.sel} style={{ width: "34rem" }} disabled={!canEdit}
            value={String(settings.values.commentsLeft || "WARN").toUpperCase() === "BLOCK" ? "BLOCK" : "WARN"}
            onChange={(e) => saveValues({ commentsLeft: e.target.value })}>
            <option value="WARN">Warn only (the approver sees them)</option>
            <option value="BLOCK">Stop sending until they are deleted</option>
          </select>
          <p className={cls.hint}>Comments are never printed in any document. Saved as soon as you change it.</p>
        </div>
      )}
      {tab === "PARTIES" && (() => {
        // how a new draft starts (Legal can change it in the draft); saved as soon as it changes
        const d = partyDefaults(settings.values);
        const put = (change, message) => saveValues({ partyDefaults: { ...d, ...change } }, message);
        return (
          <div className={cls.panel}>
            <h2 className={cls.panelTitle}>Buyer details</h2>
            <p className={cls.lbl}>Fields</p>
            <p style={{ margin: 0 }}>{d.buyerFields.map(labelOf).join(", ")}</p>
            {canEdit && <button className={cls.btn} style={{ marginTop: ".8rem" }} onClick={() => setPick("buyerFields")}>Change…</button>}
            <label className={cls.lbl} htmlFor="lineUpCm" style={{ marginTop: "1.6rem" }}>Line up the columns (Age:, PAN No:) at</label>
            <div style={{ display: "flex", gap: ".8rem", alignItems: "center" }}>
              <input id="lineUpCm" className={cls.inp} style={{ width: "8rem" }} type="number" min="0" step="0.5" defaultValue={d.buyerLineUpCm} disabled={!canEdit}
                key={`cm-${d.buyerLineUpCm}`} onBlur={(e) => { const cm = Number(e.target.value); if (Number.isFinite(cm) && cm >= 0 && cm !== d.buyerLineUpCm) put({ buyerLineUpCm: cm }); }} />
              <span>cm from the left margin (0 = do not line up)</span>
            </div>

            <h2 className={cls.panelTitle} style={{ marginTop: "2.4rem" }}>Signatures</h2>
            <p className={cls.lbl}>Each buyer's line</p>
            <p style={{ margin: 0 }}>{d.signFields.map(labelOf).join(", ")}</p>
            {canEdit && <button className={cls.btn} style={{ marginTop: ".8rem" }} onClick={() => setPick("signFields")}>Change…</button>}
            <label className={cls.lbl} htmlFor="signNumber" style={{ marginTop: "1.6rem" }}>Number in front of each buyer</label>
            <select id="signNumber" className={cls.sel} style={{ width: "24rem" }} disabled={!canEdit} value={d.signNumber} onChange={(e) => put({ signNumber: e.target.value })}>
              <option value="DOT">1. 2. 3.</option>
              <option value="BRACKET">1) 2) 3)</option>
              <option value="NONE">No number</option>
            </select>
            <label className={cls.chk} style={{ marginTop: "1.2rem" }}>
              <input type="checkbox" checked={d.signLeadOnce} disabled={!canEdit} onChange={(e) => put({ signLeadOnce: e.target.checked })} />
              The lines right above the name line (e.g. SIGNED AND DELIVERED) print once, with buyer 1
            </label>
            <p className={cls.hint}>Every new draft starts like this; Legal can change it inside a draft. Saved as soon as you change it.</p>
          </div>
        );
      })()}
      {pick && (
        <FieldOrderModal fields={fields} initial={partyDefaults(settings.values)[pick]} saveLabel="Save"
          title={pick === "buyerFields" ? "Default fields of the buyer details" : "Default fields of each buyer's signature line"}
          hint="Tick them in the order they print. A new draft starts with these; Legal can change them in the draft."
          onCancel={() => setPick(null)}
          onSave={(keys) => { const k = pick; setPick(null); saveValues({ partyDefaults: { ...partyDefaults(settings.values), [k]: keys } }); }} />
      )}
      {tab === "SUGGESTIONS" && <>
      <p className={cls.hint} style={{ margin: "0 0 1.6rem" }}>
        Legal types a field marker in the Word file, e.g. {"{Agreement Value}"} or {"{Flat No}"}. On upload each marker becomes a blank with its
        field suggested (To confirm): a name in this list first, then the field's own name; “… in words” gives the amount in words. Legal always confirms.
      </p>
      {!markerPattern && (
        <div className={cls.sugBox} style={{ marginBottom: "1.6rem" }}>Field markers are switched off: the marker blank pattern is not in the settings yet (sql/rpc/M12_marker_rules.sql).</div>
      )}
      <div className={cls.actionbar}>
        <p className={cls.count}>{markers.length} marker name{markers.length === 1 ? "" : "s"}</p>
        <div className={cls.filters}>
          <input className={cls.inp} style={{ width: "26rem" }} placeholder="Search marker names" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search marker names" />
          {canEdit && <button className={`${cls.btn} ${cls.primary}`} onClick={() => setEditing({ rule: { isActive: true }, index: -1 })}>+ Add marker name</button>}
        </div>
      </div>
      <div className={cls.tblw}>
        <table className={cls.tbl}>
          <thead>
            <tr><th>Name in the Word file</th><th>Suggests</th><th>From</th><th>Active</th><th className={cls.last} /></tr>
          </thead>
          <tbody>
            {markers.map(({ r, index }) => (
              <tr key={`${r.pattern}-${index}`}>
                <td className={`${cls.main} ${cls.mono}`}>{`{${r.pattern}}`}</td>
                <td>{labelOf(r.fieldKey)} <span className={cls.muted}>· {r.fieldKey}</span></td>
                <td>{FROM[r.source] || r.source || ""}</td>
                <td>{r.isActive === false ? "No" : "Yes"}</td>
                <td className={cls.last}>{canEdit && <button className={cls.pillEdit} onClick={() => setEditing({ rule: r, index })}>Edit</button>}</td>
              </tr>
            ))}
            {!markers.length && <tr><td colSpan={5} className={cls.empty}>{query ? "No marker name matches" : "No marker names yet"}</td></tr>}
          </tbody>
        </table>
      </div>
      <p className={cls.hint} style={{ marginTop: "1.6rem" }}>
        A marker name Legal gives a field by hand is learned when the version is approved, and suggested on later uploads.
      </p>
      </>}
      {editing && <MarkerModal rule={editing.rule} fields={fields} onCancel={() => setEditing(null)} onSave={(rule) => save(rule, editing.index)} />}
    </div>
  );
}

export default TemplateSettings;
